import { useEffect, useState } from 'react';

import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { P, match } from 'ts-pattern';

import { TAgentLocation } from '@cloud-wave/neon-common-lib';

import useHookForm from 'lib/common/hooks/useHookForm/useHookForm';

import Button from 'lib/common/components/Button';
import Icon from 'lib/common/components/Icon';
import Text from 'lib/common/components/Text';

import { useAgentPreferencesContext } from '../Context';
import { ConfirmCallbackNumberAndCandidateAddresses, ConfirmModal } from './components';
import { DEFAULT_LOCATION } from './constants';
import { myLocationFormFields } from './constants/myLocationFormFields';
import { MyLocationFormStep } from './enums/MyLocationFormStep';
import {
  TExtendedAgentPreferenceFormSchema,
  extendedAgentPreferenceFormSchema,
} from './schema/agentPreferenceFormSchema';
import { MyLocationFormProps, TCandidateAddress, TValidateAddressResponse } from './types';
import { hasBeen90DaysSinceUpdated, setLocationValues } from './utils';

import styles from './my-location-form.module.scss';

export default function MyLocationForm({ closeModal, editMode }: MyLocationFormProps) {
  const [step, setStep] = useState<MyLocationFormStep>(MyLocationFormStep.FORM);
  const [candidateAddresses, setCandidateAddresses] = useState<TCandidateAddress[]>([]);
  const [shouldConfirmCallbackNumber, setShouldConfirmCallbackNumber] = useState(false);

  const {
    state: { agentPreference },
    actions: { updatePreference, validateAddress },
  } = useAgentPreferencesContext();

  const { useForm, HookFormInput } = useHookForm();

  const {
    control,
    setValue,
    formState: { errors, isValid },
    setError,
    clearErrors,
    getValues,
    trigger,
  } = useForm<TExtendedAgentPreferenceFormSchema>({
    defaultValues: {
      location: (agentPreference.location as TAgentLocation) ?? DEFAULT_LOCATION,
      callbackNumber: agentPreference.callbackNumber ?? '',
      confirmCallbackNumber: '',
      updatedLocation: null,
    },
    mode: 'onBlur',
    resolver: yupResolver(extendedAgentPreferenceFormSchema),
  });

  //This is required to ensure the form data is set correctly when the modal is opened and the data isn't ready or page refreshed
  useEffect(() => {
    if (agentPreference.location) {
      setLocationValues(setValue, agentPreference.location as TAgentLocation, agentPreference.callbackNumber);
    }
  }, [agentPreference.location, setValue, editMode, agentPreference]);

  const handleFormSubmit = async () => {
    const currentNumberMatchesRecords = agentPreference.callbackNumber === getValues('callbackNumber');
    const confirmNumberMatchesEntered = getValues('confirmCallbackNumber') === getValues('callbackNumber');

    const shouldConfirmCallbackNumberValue = !currentNumberMatchesRecords && !confirmNumberMatchesEntered;

    setShouldConfirmCallbackNumber(shouldConfirmCallbackNumberValue);

    const { callbackNumber, location, updatedLocation } = getValues();

    let validateAddressResult: TValidateAddressResponse | null = null;

    if (step === MyLocationFormStep.FORM) {
      validateAddressResult = await validateAddress(location);

      setCandidateAddresses(validateAddressResult?.candidateAddresses || []);
    }

    if (shouldConfirmCallbackNumberValue || validateAddressResult?.candidateAddresses?.length) {
      setStep(MyLocationFormStep.CALLBACK_AND_ADDRESS_CONFIRM);
      return;
    }

    await updatePreference({ location: updatedLocation || location, callbackNumber });
    closeModal();
  };

  if (step === MyLocationFormStep.CLEAR_CONFIRM) {
    return (
      <ConfirmModal
        message={
          <>
            Not adding your location may impact the accuracy of emergency calls. Providing it helps responders reach you
            faster in an emergency.
            <br />
            <br /> Would you still like to clear your location?
          </>
        }
        onCancel={() => setStep(MyLocationFormStep.FORM)}
        onConfirm={async () => {
          await updatePreference({ location: null, callbackNumber: '' });
          closeModal();
        }}
        confirmLabel="Clear Anyway"
        cancelLabel="Keep information"
        danger
      />
    );
  }

  if (step === MyLocationFormStep.SKIP_CONFIRM) {
    return (
      <ConfirmModal
        message={
          <>
            Not adding your location may impact the accuracy of emergency calls. Providing it helps responders reach you
            faster in an emergency.
            <br />
            <br /> Would you like to skip without adding it?
          </>
        }
        onCancel={() => setStep(MyLocationFormStep.FORM)}
        onConfirm={() => closeModal()}
        confirmLabel="Skip Anyway"
        cancelLabel="Add Information"
        danger
      />
    );
  }

  if (step === MyLocationFormStep.CALLBACK_AND_ADDRESS_CONFIRM) {
    const numbersMatch = getValues('confirmCallbackNumber') === getValues('callbackNumber');
    const validateCallbackNumber = () => {
      if (
        step === MyLocationFormStep.CALLBACK_AND_ADDRESS_CONFIRM &&
        getValues('confirmCallbackNumber') !== getValues('callbackNumber')
      ) {
        setError('confirmCallbackNumber', {
          type: 'manual',
          message: 'This number doesn’t match.',
        });
        return false;
      }

      clearErrors('confirmCallbackNumber');
      return true;
    };
    return (
      <ConfirmCallbackNumberAndCandidateAddresses
        props={{
          control,
          setValue,
          errors,
          clearErrors,
          validateCallbackNumber,
          numbersMatch,
          handleFormSubmit,
          setStep,
          HookFormInput,
          candidateAddresses,
          shouldConfirmCallbackNumber,
          getValues,
        }}
      />
    );
  }

  return (
    <div className={styles['my-location-form__container']}>
      <div className={styles['my-location-form__content']}>
        {match({ is90Days: hasBeen90DaysSinceUpdated(agentPreference.e911UpdatedTimestamp), editMode })
          .with({ is90Days: true }, () => (
            <Text className="mb-10">
              To ensure accurate emergency response, we need you to confirm your information is up to date.
            </Text>
          ))
          .with({ is90Days: false, editMode: P.when((v) => !v) }, () => (
            <Text className="mb-10">You can update this anytime in your preferences.</Text>
          ))
          .otherwise(() => null)}
        <Text type="heading3">My Location</Text>

        {myLocationFormFields.map((field, index) => (
          <HookFormInput
            {...(index === 0 ? { autoFocus: true } : {})}
            key={field.name}
            label={field.label}
            InputLabelProps={{ shrink: true, required: field.required }}
            name={field.name}
            data-testid={field.name.replace('location.', '')}
            validationError={errors[field.name]}
            clearErrors={clearErrors}
            fullWidth
            control={control}
            placeholder={field.placeholder}
            {...(field.type === 'SELECT'
              ? {
                  onSelectChange: ({ value }: { value: string }) =>
                    setValue(field.name as keyof TExtendedAgentPreferenceFormSchema, value),
                  type: 'SELECT',
                  options: field.options,
                }
              : {
                  onBlur: (e) =>
                    setValue(field.name as keyof TExtendedAgentPreferenceFormSchema, e.target.value.trim()),
                })}
          />
        ))}

        <div className="next-to-10 align-normal flex-column">
          <div className="flex next-to-5">
            <Text type="heading3">My Callback Number</Text>
            <Icon
              icon={faInfoCircle}
              height={15}
              width={15}
              tooltip={
                'Emergency services will call this number directly if they need to follow up. It won’t go through your contact centre.'
              }
            />
          </div>
          <HookFormInput
            InputLabelProps={{ shrink: true, required: true }}
            name="callbackNumber"
            data-testid="callbackNumber"
            validationError={errors['callbackNumber']}
            onBlur={() => {
              trigger('callbackNumber');
            }}
            clearErrors={clearErrors}
            fullWidth
            control={control}
            ariaRequired
            type="PHONE"
            required
          />
        </div>
      </div>
      <section aria-label="footer" className={styles['my-location-form__footer']}>
        <div>
          {agentPreference.location && (
            <Button
              disabled={!getValues()}
              onClick={() => setStep(MyLocationFormStep.CLEAR_CONFIRM)}
              styleType="SECONDARY"
            >
              Clear
            </Button>
          )}
        </div>
        <div className="justify-end next-to-10">
          {!agentPreference.location && (
            <Button
              onClick={() => setStep(MyLocationFormStep.SKIP_CONFIRM)}
              styleType="SECONDARY_DANGER"
              data-testid="formSkip"
            >
              Skip
            </Button>
          )}
          <Button data-testid="formSave" disabled={!isValid} asyncAction onClick={() => handleFormSubmit()}>
            {!agentPreference.location ? 'Continue' : 'Save'}
          </Button>
        </div>
      </section>
    </div>
  );
}
